import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/app/layouts/PageContainer";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { PriceTableRatesEditor } from "@/components/price-table/PriceTableRatesEditor";
import { PasteRowsModal } from "@/components/price-table/PasteRowsModal";
import { TestPricePanel } from "@/components/price-table/TestPricePanel";
import { priceTablesAdminApi, priceTableKeys, usePriceTableDetail } from "@/lib/price-tables";
import { fromDetailRows, toReplaceBody, type PriceTableRowDraft } from "@/lib/price-table-rows";
import { ChevronLeft } from "lucide-react";

export function PriceTableDetailPage() {
  const { id } = useParams();
  const ptId = Number(id);
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canWrite = has("price-tables:write");
  const { data, isLoading } = usePriceTableDetail(ptId);
  const [rows, setRows] = useState<PriceTableRowDraft[]>([]);
  const [pasteOpen, setPasteOpen] = useState(false);

  useEffect(() => { if (data) setRows(fromDetailRows(data.rows)); }, [data]);

  const rateTypesQ = useQuery({
    queryKey: ["price-table-rate-types", "names"],
    queryFn: async () => {
      const res = await apiFetch<{ rateType: string }[] | { items: { rateType: string }[] }>("/api/lookups/price-table-rate-types?page=0&pageSize=200");
      const list = Array.isArray(res) ? res : res.items;
      return list.map((x) => x.rateType);
    },
  });

  const save = useMutation({
    mutationFn: () => priceTablesAdminApi.replaceRows(ptId, toReplaceBody(rows)),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: priceTableKeys.detail(ptId) }); toast.success("Rates saved"); },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  if (isLoading) return <PageContainer><p>Loading…</p></PageContainer>;

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to="/admin/bom/price-tables" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-1">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Price tables
          </Link>
          <h1 className="text-xl font-semibold">{data?.description} <span className="text-muted-foreground">#{ptId}</span></h1>
        </div>
        <div className="flex gap-2">
          {canWrite && <Button variant="outline" onClick={() => setPasteOpen(true)}>Paste from spreadsheet</Button>}
          {canWrite && <Button onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>}
        </div>
      </div>

      <PriceTableRatesEditor value={rows} onChange={setRows} rateTypes={rateTypesQ.data ?? []} disabled={!canWrite} />

      <div className="mt-6">
        <TestPricePanel priceTableId={ptId} rateTypes={rateTypesQ.data ?? []} />
      </div>

      <PasteRowsModal open={pasteOpen} onOpenChange={setPasteOpen}
        onApply={(pasted, mode) => setRows((cur) => (mode === "replace" ? pasted : [...cur, ...pasted]))} />
    </PageContainer>
  );
}
